<template>
  <!-- XOL-586 (D-2026-10-16c, F5, Q12): who one request e-mail went to, and what became of
       each address. READ-ONLY for every viewer: there is no resend here (the only way to
       send again is step 2's / step 5's "Re-send {status} e-Mail", which opens a recipient
       picker). The data is GET /actions/{id}/mail/{batch}, fetched on every open.

       Delta 4: the header (InfoDialogHeader) carries the e-mail's name, its result tag and the
       file; four tiles count the addresses; "By party" says how many each party got; the facts
       are an InfoMetaList; the table has two columns (Recipient, Status).

       XOL-645: on a server that captures mail instead of sending it, a row whose API answer says
       `has_preview === true` carries a "View e-mail" button that opens MailPreviewDialog (the
       stored copy), stacked over this dialog. Anywhere else (production always) the button, its
       wrapper and the dialog's fetch do not exist.

       Standalone: the step 2 / 5 last-sent line opens it today, the Activity log opens it
       from a mail row. The parent owns `visible` and closes it on `close`. -->
  <el-dialog
    :visible="visible"
    :title="title"
    append-to-body
    :width="isMediumOrLess ? '92%' : '760px'"
    top="6vh"
    custom-class="xol-mail-batch-dialog xol-info-dialog"
    @open="onOpen"
    @opened="onOpened"
    @close="onDialogClose"
    @closed="onClosed">

    <info-dialog-header slot="title" ref="header"
      icon="fa-envelope"
      :title="headerTitle"
      :subtitle="subtitle"
      :subtitle-title="fileName"
      :tag="batch ? result.tag : null"
      :sr-text="srText"
      :compact="isMediumOrLess" />

    <div class="mail-batch" ref="body" tabindex="-1"
      v-loading="loading" :element-loading-text="text('requestMail.batch.loading')">

      <div v-if="error" class="mail-batch__error">
        <el-alert type="error" :title="errorText" show-icon :closable="false" />
        <el-button v-if="error === 'generic'" size="small" class="mail-batch__retry" @click="load">{{text('requestMail.batch.retry')}}</el-button>
      </div>

      <template v-if="batch">

        <div class="mail-stats" :class="{ 'is-narrow': isMediumOrLess }" role="group" :aria-label="result.text">
          <div v-for="s in stats" :key="s.key" class="mail-stat" :class="['is-' + s.key, { 'is-zero': s.value === 0 }]">
            <div class="mail-stat__value">{{s.value}}</div>
            <div class="mail-stat__label"><i v-if="s.icon" class="fa" :class="s.icon" aria-hidden="true"></i><span>{{s.label}}</span></div>
          </div>
        </div>

        <div v-if="parties.length > 0" class="mail-parties">
          <span class="mail-parties__label">{{text('requestMail.batch.parties.label')}}</span>
          <span v-for="p in parties" :key="p.party" class="mail-party" :class="{ 'is-zero': p.n === 0 }" role="group" :aria-label="p.aria">
            <b>{{p.org || p.partyText}}</b>
            <span v-if="p.org" class="mail-party__party">{{p.partyText}}</span>
            <b class="mail-party__n">{{p.n}}</b>
          </span>
        </div>

        <p v-if="counts.queued > 0" class="mail-batch__queued">
          <small>{{text('requestMail.batch.queued')}}</small>
          <el-button type="text" size="mini" icon="fa fa-sync-alt" :disabled="loading" @click="load">{{text('requestMail.batch.refresh')}}</el-button>
        </p>

        <info-meta-list :rows="metaRows" :narrow="isMediumOrLess" />

        <el-table v-if="rows.length > 0" class="mail-batch__table" size="small" :data="rows" :row-class-name="rowClass" style="width: 100%">

          <el-table-column :label="text('requestMail.col.recipient')" min-width="300">
            <template slot-scope="scope">
              <b class="mail-batch__email">{{scope.row.email}}</b>
              <small class="mail-batch__line mail-batch__org">{{scope.row.lineTwo}}</small>
              <small v-if="scope.row.via === 'additional'" class="mail-batch__line mail-batch__aux">{{text('requestMail.detail.viaAdditional')}}</small>
              <small v-if="scope.row.delivered_to" class="mail-batch__line mail-batch__aux">{{text('requestMail.detail.redirect', { address: scope.row.delivered_to })}}</small>

              <!-- XOL-645: v-if, never v-show: a row without a stored copy renders exactly as before. -->
              <div v-if="scope.row.hasPreview" class="mail-batch__preview">
                <el-button type="text" size="mini" icon="fa fa-eye" aria-haspopup="dialog"
                  :aria-label="text('requestMail.preview.viewAria', { email: scope.row.email })"
                  @click="openPreview(scope.row, $event)">{{text('requestMail.preview.view')}}</el-button>
              </div>

              <!-- Phone width: the table has one column; the status stacks under the address. -->
              <div v-if="isMediumOrLess" class="mail-status mail-status--stacked">
                <el-tag size="mini" effect="light" :type="scope.row.tag.type"><i class="fa" :class="scope.row.tag.icon" aria-hidden="true"></i><span>{{scope.row.tag.text}}</span></el-tag>
                <small v-if="scope.row.detail" class="mail-status__detail">{{scope.row.detail}}</small>
                <el-popover v-if="scope.row.error" trigger="click" width="320" :title="text('requestMail.detail.serverMessageTitle')">
                  <pre class="mail-batch__error-text">{{scope.row.error}}</pre>
                  <el-button slot="reference" type="text" size="mini">{{text('requestMail.detail.serverMessage')}}</el-button>
                </el-popover>
                <small v-if="scope.row.status === 'failed' && !scope.row.addressFine" class="mail-batch__line mail-batch__aux">{{text('requestMail.detail.fixHint')}}</small>
              </div>
            </template>
          </el-table-column>

          <el-table-column v-if="!isMediumOrLess" key="status" :label="text('requestMail.col.status')" min-width="220">
            <template slot-scope="scope">
              <div class="mail-status">
                <el-tag size="mini" effect="light" :type="scope.row.tag.type"><i class="fa" :class="scope.row.tag.icon" aria-hidden="true"></i><span>{{scope.row.tag.text}}</span></el-tag>
                <small v-if="scope.row.detail" class="mail-status__detail">{{scope.row.detail}}</small>
                <el-popover v-if="scope.row.error" trigger="click" width="320" :title="text('requestMail.detail.serverMessageTitle')">
                  <pre class="mail-batch__error-text">{{scope.row.error}}</pre>
                  <el-button slot="reference" type="text" size="mini">{{text('requestMail.detail.serverMessage')}}</el-button>
                </el-popover>
                <small v-if="scope.row.status === 'failed' && !scope.row.addressFine" class="mail-batch__line mail-batch__aux">{{text('requestMail.detail.fixHint')}}</small>
              </div>
            </template>
          </el-table-column>

        </el-table>

        <p v-else class="mail-batch__none">{{text('requestMail.batch.none')}}</p>

        <p class="info-footnote"><i class="fa fa-info-circle" aria-hidden="true"></i><span>{{text('requestMail.batch.helper')}}</span></p>

      </template>
    </div>

    <!-- XOL-645: the stored copy of one e-mail, stacked over this dialog. Closing this dialog (or a
         refresh that no longer lists the row) clears `previewRow`, which closes it. The values
         come from `previewShown`, which outlives `previewRow`, so the title does not empty
         itself while the dialog animates out; they are dropped on its @closed. v-if: nothing exists
         until a click. A hidden el-dialog that was never opened is not moved to the body, so it
         would sit inside this dialog (a second Close button in its footer). It is mounted CLOSED
         and opened on the next tick (openPreview): Vue's <transition> skips `enter` on a first
         render, so a dialog mounted already visible never emits @open / @opened (no focus move,
         no fade). -->
    <mail-preview-dialog v-if="previewShown"
      :visible="!!previewRow"
      :action-id="actionId"
      :batch-id="batchId"
      :log-id="previewShown ? previewShown.id : null"
      :email="previewShown ? previewShown.email : ''"
      :delivered-to="previewShown ? previewShown.delivered_to : null"
      :subject="batch ? batch.subject : null"
      :label="headerTitle"
      :recipient-line="previewShown ? previewShown.lineTwo : null"
      @close="previewRow = null"
      @closed="previewLast = null" />

    <!-- Read-only for every viewer: Close is the only button, and it is never disabled. -->
    <div slot="footer" class="mail-batch__footer">
      <el-button size="small" :class="{ 'is-block': isMediumOrLess }" @click="requestClose">{{text('requestMail.footer.close')}}</el-button>
    </div>

  </el-dialog>
</template>

<script>
import axios from '@/lib/axiosClient'
import DateMixins from '@/mixins/DateMixins'
import InfoDialogHeader from '@/components/requests/InfoDialogHeader'
import InfoMetaList from '@/components/requests/InfoMetaList'
import MailPreviewDialog from '@/components/requests/MailPreviewDialog'
import { activityText, mailLabel, mailResult, mailTriggerText, mailRecipientStatus, partyCounts, shortFileName, QUEUE_FAILURE_CODES } from '@/lib/requestActivity'
import { stepName as rtaStepName } from '@/lib/rtaStage'

var PARTY_ORDER = { towerco: 0, owner: 1, provider: 2 }
var STATUS_ORDER = { failed: 0, queued: 1, sent: 2 }

export default {
  name: 'mail-batch-dialog',

  mixins: [DateMixins],

  components: {
    InfoDialogHeader,
    InfoMetaList,
    MailPreviewDialog
  },

  props: {
    // The request the batch belongs to (a batch of another request answers 404).
    actionId: {
      type: [Number, String],
      default: null
    },

    batchId: {
      type: String,
      default: null
    },

    // The parent owns it and clears it on `close`.
    visible: {
      type: Boolean,
      default: false
    },

    // The request's steps: names "Step 2: Technical Proposal". Optional.
    steps: {
      type: Array,
      default: function () { return [] }
    },

    // The request type, for "turned off for this {rtx}". Optional.
    rtx: {
      type: String,
      default: 'request'
    },

    // The approval status the e-mail carried, for its label. Optional: without it
    // (and without one in the answer) the label is the bare "Approval e-mail".
    status: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      data: null,
      loading: false,
      // null | 'generic' | 'notFound' | 'forbidden'
      error: null,
      seq: 0,
      opened: false,
      opener: null,
      // XOL-645: the row whose stored e-mail is open (null = closed), and the last one that was,
      // kept so the closing dialog still has its values.
      previewRow: null,
      previewLast: null
    }
  },

  computed: {
    batch () {
      return this.data && this.data.batch ? this.data.batch : null
    },

    recipients () {
      return this.data && Array.isArray(this.data.recipients) ? this.data.recipients : []
    },

    fileName () {
      var f = this.batch ? this.batch.file : null

      return f && typeof f === 'object' ? (f.name || null) : (f || null)
    },

    // The organisation a test e-mail was addressed to ("{org} ({party})").
    // A test that reached nobody (every address skipped) has no recipients: the organisation
    // then comes from the first skipped entry, and failing that the title names the party.
    testOrgName () {
      var first = this.recipients[0]
      var skipped = this.batch && Array.isArray(this.batch.skipped) ? this.batch.skipped[0] : null
      var org = (first && first.organisation) || (skipped && skipped.organisation) || null

      return org ? (org.shortName || org.name || null) : null
    },

    testOrg () {
      if (this.testOrgName) {
        return this.testOrgName
      }

      var first = this.recipients[0]
      var party = this.batch ? (this.batch.party || (first && first.party)) : null

      return party ? this.partyText(party) : null
    },

    title () {
      if (!this.batch) {
        return this.text('requestMail.label.other')
      }

      return mailLabel(this.batch.kind, {
        status: this.status || this.batch.approval_status || null,
        file: this.fileName,
        org: this.testOrg,
        event: this.batch.event,
        optionNo: this.batch.option_no
      })
    },

    // The header's title: the e-mail's name without its file ("Upload notice"); a test and an
    // approval keep what makes them different ("Test e-mail (TLB)"). "E-mail" before there is one.
    headerTitle () {
      if (!this.batch) {
        return this.text('requestMail.label.other')
      }

      return mailLabel(this.batch.kind, {
        status: this.status || this.batch.approval_status || null,
        file: null,
        org: this.testOrg,
        event: this.batch.event,
        optionNo: this.batch.option_no
      })
    },

    // The file, in the middle-shortened form (the full name is the tooltip).
    subtitle () {
      return this.fileName ? shortFileName(this.fileName, this.isMediumOrLess ? 28 : 56) : null
    },

    // The one sentence a screen reader gets for the header: "Upload notice (TP_rev2.pdf) sent to 8 recipients".
    srText () {
      return this.batch ? this.title + ' ' + this.result.text : null
    },

    stats () {
      var c = this.counts

      return [
        { key: 'total', label: this.text('requestMail.batch.stat.recipients'), icon: null, value: c.sent + c.queued + c.failed },
        { key: 'sent', label: this.text('requestMail.batch.stat.sent'), icon: 'fa-check', value: c.sent },
        { key: 'queued', label: this.text('requestMail.batch.stat.queued'), icon: 'fa-clock', value: c.queued },
        { key: 'failed', label: this.text('requestMail.batch.stat.failed'), icon: 'fa-times-circle', value: c.failed }
      ]
    },

    counts () {
      var c = { queued: 0, sent: 0, failed: 0 }

      this.recipients.forEach(function (r) {
        if (Object.prototype.hasOwnProperty.call(c, r.status)) {
          c[r.status]++
        }
      })

      return c
    },

    failedCodes () {
      var codes = {}

      this.recipients.forEach(function (r) {
        if (r.status === 'failed' && r.error_code) {
          codes[r.error_code] = (codes[r.error_code] || 0) + 1
        }
      })

      return codes
    },

    result () {
      return mailResult({
        counts: this.counts,
        repeat: !!(this.batch && this.batch.repeat),
        subset: !!(this.batch && this.batch.subset),
        failed_codes: this.failedCodes,
        // D31: the API's verdict on the batch; without it the tag comes from the counts.
        outcome: this.batch ? this.batch.outcome : null
      }, { rtx: this.rtx })
    },

    // Delta 3 / 4: how many addresses each party got, [{ party, org, partyText, n, aria }]. The API's
    // `parties` when it sends them, else the recipients grouped by party plus the skipped parties
    // at 0. Empty (the row is hidden) for a test, which goes to one party, and for an answer that
    // carries no party counts.
    parties () {
      var self = this

      if (!this.batch || this.batch.kind === 'test') {
        return []
      }

      return partyCounts({
        parties: this.batch.parties,
        recipients: this.recipients,
        skipped: this.batch.skipped
      }).map(function (p) {
        var party = self.partyText(p.party)

        return {
          party: p.party,
          org: p.org,
          partyText: party,
          n: p.n,
          aria: self.text('requestMail.batch.party.aria', { org: p.org, party: party, n: p.n })
        }
      })
    },

    // One row of the meta list per fact the batch has. The time, the file and the parties are
    // in the header, the tiles and the Triggered row.
    metaRows () {
      var b = this.batch
      var rows = []

      if (!b) {
        return rows
      }

      rows.push({
        key: 'trigger',
        label: this.text('requestMail.batch.meta.trigger'),
        value: this.text('requestMail.batch.meta.triggerValue', {
          trigger: mailTriggerText(b, { short: true }),
          datetime: b.created_at ? this.formatDateTime(b.created_at) : ''
        })
      })

      if (b.step_no) {
        rows.push({ key: 'step', label: this.text('requestMail.batch.meta.step'), value: this.stepText(b.step_no) })
      }

      // Delta 1 (item 4): a Re-send to selected recipients says how many of the current ones
      // were chosen. `offered` is the count the server could have mailed at that moment (null
      // on a batch recorded without it).
      if (b.subset) {
        var offered = b.offered === null || b.offered === undefined ? null : Number(b.offered)

        rows.push({
          key: 'recipients',
          label: this.text('requestMail.batch.meta.recipients'),
          value: offered === null || isNaN(offered)
            ? this.text('requestMail.batch.subsetUnknown')
            : this.text('requestMail.batch.subset', { m: this.recipients.length, n: offered })
        })
      }

      if (b.kind === 'test' && this.testOrgName) {
        rows.push({
          key: 'org',
          label: this.text('requestMail.batch.meta.organisation'),
          value: this.testOrgName + ' (' + this.partyText(b.party || (this.recipients[0] && this.recipients[0].party)) + ')'
        })
      }

      if (b.subject) {
        rows.push({ key: 'subject', label: this.text('requestMail.batch.meta.subject'), value: b.subject })
      }

      return rows
    },

    // Failed first, then queued, then sent; then Tower Co, Telco, Provider; then e-mail.
    rows () {
      var self = this
      var batchAt = this.batch ? this.batch.created_at : null

      return this.recipients.map(function (r) {
        var org = r.organisation || {}
        var orgShort = org.shortName || org.name || ''
        var orgPart = orgShort ? orgShort + ' (' + self.partyText(r.party) + ')' : self.partyText(r.party)
        var contactName = r.contact && r.contact.name ? r.contact.name : null
        var status = mailRecipientStatus(r, batchAt, { formatDateTime: self.formatDateTime })

        return {
          id: r.id,
          email: r.email,
          contactName: contactName,
          via: r.via,
          delivered_to: r.delivered_to,
          error: r.error ? String(r.error) : null,
          status: r.status,
          // The address is fine when the queue failed it, or the e-mail couldn't be built (a server
          // error): "Fix the address" only fits the others.
          addressFine: r.status === 'failed' && (QUEUE_FAILURE_CODES.indexOf(r.error_code) !== -1 || r.error_code === 'unmapped_kind'),
          party: r.party,
          // "Aisyah R. · TLB (Telco)", or "TLB (Telco)" for an address with no contact.
          lineTwo: contactName ? contactName + ' · ' + orgPart : orgPart,
          tag: status.tag,
          detail: status.detail,
          // XOL-645: strictly `true`. Absent (an API without XOL-645) or anything else = no button.
          hasPreview: r.has_preview === true
        }
      }).sort(function (a, b) {
        var byStatus = (STATUS_ORDER[a.status] === undefined ? 3 : STATUS_ORDER[a.status]) - (STATUS_ORDER[b.status] === undefined ? 3 : STATUS_ORDER[b.status])
        if (byStatus !== 0) return byStatus

        var byParty = (PARTY_ORDER[a.party] === undefined ? 3 : PARTY_ORDER[a.party]) - (PARTY_ORDER[b.party] === undefined ? 3 : PARTY_ORDER[b.party])
        if (byParty !== 0) return byParty

        return String(a.email).toLowerCase() < String(b.email).toLowerCase() ? -1 : 1
      })
    },

    previewShown () {
      return this.previewRow || this.previewLast
    },

    // What the dialog is showing: null while it is closed.
    fetchKey () {
      return this.visible && this.batchId ? this.actionId + '/' + this.batchId : null
    },

    errorText () {
      if (this.error === 'forbidden') {
        return this.text('requestMail.batch.forbidden', { towerCo: this.towerCoName() })
      }

      return this.text(this.error === 'notFound' ? 'requestMail.batch.notFound' : 'requestMail.batch.error')
    }
  },

  watch: {
    // Every open fetches again: nothing is cached between opens. The same dialog pointed at
    // another batch while it is open fetches that one. ONE watcher on both: a parent that
    // opens the dialog and sets its batch in the same tick (the Activity log) must cost one
    // GET, not two.
    fetchKey (now) {
      if (now) {
        this.load()
      } else {
        this.seq++
        this.opened = false
        // XOL-645: closing this dialog closes the e-mail stacked over it.
        this.previewRow = null
      }
    }
  },

  mounted () {
    if (this.visible) {
      this.opener = typeof document !== 'undefined' ? document.activeElement : null
      this.load()
    }
  },

  methods: {
    text (key, params) {
      return activityText(key, params)
    },

    partyText (party) {
      var key = 'requestMail.party.' + party

      try {
        return activityText(key)
      } catch (e) {
        return party || ''
      }
    },

    // "Step 2: Technical Proposal" from the request's party-1 step of that number. An RTA has no
    // step rows: its five steps are named by rtaStage (XOL-67: "Step 4: Tower Co Decision").
    stepText (n) {
      var title = null

      ;(this.steps || []).forEach(function (s) {
        if (Number(s.stepNo) === Number(n) && Number(s.partyNo) === 1 && s.stepName) {
          title = s.stepName
        }
      })

      if (!title && this.rtx === 'RTA') {
        title = rtaStepName(n) || null
      }

      return this.text('requestMail.batch.meta.stepValue', { n: n, title: title })
    },

    // A failed address's whole row is tinted (and keeps the tint on hover).
    rowClass (ctx) {
      return ctx && ctx.row && ctx.row.status === 'failed' ? 'is-failed' : ''
    },

    load () {
      var self = this
      var seq = ++this.seq

      if (!this.actionId || !this.batchId) {
        return Promise.resolve()
      }

      this.loading = true
      this.error = null

      return axios.get(this.getEndpoint('/actions/' + this.actionId + '/mail/' + this.batchId)).then(function (response) {
        if (seq !== self.seq) {
          return
        }

        var body = response ? response.data : null

        if (body && !body.batch && body.data) {
          body = body.data
        }

        if (!body || !body.batch) {
          self.fail('generic', null)
          return
        }

        self.data = body
        self.loading = false
        self.dropMissingPreview()
        self.focusSummary()
      }, function (err) {
        if (seq !== self.seq) {
          return
        }

        var status = err && err.response ? err.response.status : null

        console.error('XOL-586: could not load the e-mail batch', status, err)
        self.fail(status === 403 ? 'forbidden' : (status === 404 ? 'notFound' : 'generic'), status)
      })
    },

    fail (kind) {
      this.error = kind
      this.loading = false

      // A batch that is gone or off limits shows nothing of the one shown before.
      if (kind !== 'generic') {
        this.data = null
        this.previewRow = null
      }
    },

    // XOL-645: a refresh that no longer lists the row whose e-mail is open closes that e-mail.
    // `load` focuses the header right after (focusSummary): the row the preview would return
    // focus to is gone.
    dropMissingPreview () {
      var row = this.previewRow

      if (!row) {
        return
      }

      var still = this.recipients.some(function (r) { return String(r.id) === String(row.id) })

      if (!still) {
        this.previewRow = null
      }
    },

    // XOL-645: "View e-mail". Safari does not focus a button on click, so focus it first: the
    // preview records document.activeElement when it opens, and returns focus to it on close.
    openPreview (row, event) {
      var el = event && event.currentTarget

      if (el && typeof el.focus === 'function') {
        el.focus()
      }

      // Mount it closed (previewLast), open it on the next tick (previewRow): see the template.
      var self = this

      this.previewLast = row
      this.$nextTick(function () {
        if (self.previewLast === row) {
          self.previewRow = row
        }
      })
    },

    // Focus lands on the header once the dialog has opened (and again when the answer is in).
    focusSummary () {
      var self = this

      if (!this.opened) {
        return
      }

      this.$nextTick(function () {
        var el = self.$refs.header || self.$refs.body

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    onOpen () {
      this.opener = typeof document !== 'undefined' ? document.activeElement : null
    },

    onOpened () {
      this.opened = true
      this.focusSummary()
    },

    // Element closed itself (the ×, Esc, the backdrop): hand it to the parent. A close the
    // parent started (`visible` already false) is not announced twice.
    onDialogClose () {
      if (this.visible) {
        this.$emit('close')
      }
    },

    requestClose () {
      this.$emit('close')
    },

    // The dialog has closed: focus goes back to what opened it.
    onClosed () {
      var opener = this.opener

      this.opened = false
      this.opener = null

      if (opener && typeof opener.focus === 'function' && typeof document !== 'undefined' && document.body.contains(opener)) {
        opener.focus()
      }
    }
  }
}
</script>

<style scoped>
.mail-batch { min-height: 200px; outline: none; }
.mail-batch__retry { margin-top: 8px; }

/* The four tiles. All four always show, so nothing jumps; a 0 is grey, and the word beside it
   says what it counts. */
.mail-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin: 0 0 10px; }
.mail-stats.is-narrow { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.mail-stat { background: #F5F7FA; border-radius: 4px; padding: 10px 12px; }
.mail-stat__value { font-size: 22px; line-height: 28px; font-weight: bold; font-variant-numeric: lining-nums tabular-nums; color: #303133; }
.mail-stat__label { font-size: 12px; line-height: 16px; color: #606266; }
.mail-stat__label .fa { margin-right: 5px; }
.mail-stat.is-sent .mail-stat__value, .mail-stat.is-sent .fa { color: #67C23A; }
.mail-stat.is-queued .mail-stat__value, .mail-stat.is-queued .fa { color: #409EFF; }
.mail-stat.is-failed .mail-stat__value, .mail-stat.is-failed .fa { color: #F56C6C; }
.mail-stat.is-zero .mail-stat__value, .mail-stat.is-zero .fa { color: #C0C4CC; }

.mail-parties { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 8px; margin: 0 0 12px; }
.mail-parties__label { font-size: 12px; color: #909399; }
.mail-party { display: inline-flex; align-items: baseline; gap: 6px; padding: 3px 10px; border: 1px solid #EBEEF5; border-radius: 4px; font-size: 13px; color: #303133; }
.mail-party__party { font-size: 12px; color: #909399; }
.mail-party__n { font-variant-numeric: lining-nums tabular-nums; color: #303133; }
.mail-party.is-zero .mail-party__n { color: #909399; }

.mail-batch__queued { margin: 0 0 8px; }
.mail-batch__queued small { font-size: 12px; color: #606266; }
.mail-batch__queued ::v-deep .fa + span { margin-left: 5px; }
/* padding-bottom: el-table's .cell is overflow:hidden and this is its last line, so the 2px ring (+2px offset) would lose its bottom edge. */
.mail-batch__preview { margin-top: 2px; padding-bottom: 4px; line-height: 1; }
.mail-batch__preview ::v-deep .fa + span { margin-left: 5px; }
.mail-batch__preview ::v-deep .el-button:focus-visible { outline: 2px solid #409EFF; outline-offset: 2px; border-radius: 2px; }
.mail-batch__line { display: block; }
.mail-batch__email { display: block; font-size: 13px; color: #303133; word-break: break-all; }
.mail-batch__org { font-size: 12px; line-height: 18px; color: #606266; }
.mail-batch__aux { font-size: 12px; line-height: 18px; color: #909399; }
.mail-batch__none { margin: 0; font-size: 13px; color: #606266; }
.mail-batch__error-text { white-space: pre-wrap; word-break: break-word; max-height: 200px; overflow: auto; font-size: 12px; margin: 0; }

.mail-status__detail { margin-left: 6px; font-size: 12px; color: #606266; }
.mail-status .el-tag .fa { margin-right: 5px; }
.mail-status--stacked { margin-top: 4px; }
.mail-status--stacked .mail-status__detail { margin-left: 0; display: block; margin-top: 2px; }
.mail-batch__table ::v-deep .cell { word-break: normal; overflow-wrap: break-word; }
.mail-batch__table ::v-deep .el-table__body tr.is-failed > td { background: #FEF0F0; }
.mail-batch__table ::v-deep .el-table__body tr.is-failed:hover > td { background: #FEF0F0; }
.mail-batch__table ::v-deep .el-table__body tr.is-failed > td:first-child { box-shadow: inset 3px 0 0 #F56C6C; }

.info-footnote { margin: 12px 0 0; font-size: 12px; line-height: 18px; color: #909399; }
.info-footnote .fa { margin-right: 6px; }

.mail-batch__footer { text-align: right; }
.mail-batch__footer .is-block { width: 100%; }
</style>
